/**
 * Helper to check if a function is browser native code.
 *
 * NOTE: not importing from utils package or using other utils to use this file with webpack provide plugin
 */
const isNative = (fn: unknown): boolean => typeof fn === 'function' && /\[native code\]/.test(Function.prototype.toString.call(fn));

/**
 * Get the native Promise constructor from an iframe, which is unaffected by any page-level polyfill.
 *
 * @returns Promise from an temporary iframe
 */
const getPromiseFromIframe = (): PromiseConstructor => {
    const iframe = document.createElement('iframe');
    iframe.style.display = 'none';
    (document.head ?? document.documentElement).appendChild(iframe);
    const promiseConstructor: PromiseConstructor = (iframe.contentWindow as any).Promise;
    iframe.remove();
    return promiseConstructor;
}

/**
 * list the static methods of Promise which are not baseline (i.e. not guaranteed to be present in all environments)
 * but might be added by core-js, and which we want to copy over if missing on the native Promise we get from the iframe.
 */
const PROMISE_STATIC_METHODS_WHICH_ARE_NOT_BASELINE = ['withResolvers', 'allSettled', 'any', 'try'] as const;

/**
 * Copy known static methods that are missing on the target but present on the source.
 *
 * @param target The object to which missing methods will be added.
 * @param source The object from which methods will be copied if they are missing in the target.
 */
const copyMissingMethods = (target: PromiseConstructor, source: PromiseConstructor): void => {
    for (const key of PROMISE_STATIC_METHODS_WHICH_ARE_NOT_BASELINE) {
        if ((key in source) && !(key in target)) {
            // TODO do we need to check if value is function? do we need to bind it?
            (target as any)[key] = (source as any)[key];
        }
    }
}

let NativePromise: PromiseConstructor = window.Promise;

// CASE1: window.Promise is native and core-js has already patched any missing static methods (e.g. withResolvers) onto it
// CASE2: window.Promise might be a broken page-level polyfill so recover the real native Promise via an iframe (isolated from the page scope).
if (!isNative(window.Promise)) {
    // get the native Promise constructor from an iframe, which is unaffected by any page-level polyfill
    NativePromise = getPromiseFromIframe();
    // Copy any static methods that core-js added to window.Promise
    // (e.g. withResolvers on Safari < 17.4) but are absent on the iframe's native Promise.
    // These core-js implementations call `new this(...)` internally, so bound to
    // NativePromise they work correctly even though window.Promise is broken.
    copyMissingMethods(NativePromise, window.Promise);
}

export default NativePromise;
